import { useState } from "react";
import { createContext } from "react";
import { useDispatch } from "react-redux";

import {
  setLoadingFalseAction,
  setLoadingTrueAction,
} from "../../../redux/actions/loadingActions/loadingActions";
import {
  toastErrorMessage,
  toastSuccessMessage,
} from "../../../utils/toastMessage/toastMessage";
import {
  createPassengerUserService,
  deletePassengerUserService,
  editPassengerUserService,
  getPassengersUserService,
} from "../../../services/userServices";

export const passengerUserContext = createContext({
  passengers: [],
  setPassengers: () => {},
  id: "",
  setId: () => {},
  filter: "",
  setFilter: () => {},
  sort: "",
  setSort: () => {},
  isShowModal: null,
  setIsShowModal: () => {},
  handleShowModal: () => {},
  handleHideModal: () => {},
  handleGetPassengers: () => {},
  handleCreatePassenger: () => {},
  handleEditPassenger: () => {},
  handleDeletePassenger: () => {},
});

const PassengerUserContext = ({ children }) => {
  const dispatch = useDispatch();

  const [passengers, setPassengers] = useState([]);
  const [id, setId] = useState("");
  const [filter, setFilter] = useState("");
  const [sort, setSort] = useState("");
  const [isShowModal, setIsShowModal] = useState(false);

  // Modal Operation
  const handleShowModal = (val, setValue) => {
    setIsShowModal(true);

    if (val) {
      setId(val._id);
      setValue("firstName", val.firstName);
      setValue("lastName", val.lastName);
      setValue("firstNameLatin", val.firstNameLatin);
      setValue("lastNameLatin", val.lastNameLatin);
      setValue("birthDate", val.birthDate);
      setValue("gender", val.gender);
      setValue("nationalNumber", val.nationalNumber);
      setValue("phoneNumber", val.phoneNumber);
    }
  };

  const handleHideModal = () => {
    setIsShowModal(false);
    setId("");
  };

  // Get Passengers
  const handleGetPassengers = async (event) => {
    event?.preventDefault();

    const objData = {
      filter,
      sort,
    };
    try {
      dispatch(setLoadingTrueAction());
      const { status, data } = await getPassengersUserService(objData);
      if (status === 200) {
        setPassengers(data.result?.passengers);
      }
    } catch ({ response }) {
      if (response) {
        if (response.status) {
          toastErrorMessage(response.data.resultMessage);
        }
      }
    } finally {
      dispatch(setLoadingFalseAction());
    }
  };

  // Create Passenger
  const handleCreatePassenger = async (userData) => {
    try {
      dispatch(setLoadingTrueAction());
      const { status, data } = await createPassengerUserService(userData);
      if (status === 200) {
        toastSuccessMessage(data.resultMessage);
        handleGetPassengers();
        handleHideModal();
      }
    } catch ({ response }) {
      if (response) {
        if (response.status) {
          toastErrorMessage(response.data.resultMessage);
        }
      }
      dispatch(setLoadingFalseAction());
    }
  };

  // Edit Passenger
  const handleEditPassenger = async (userData) => {
    const objData = {
      id,
      ...userData,
    };

    try {
      dispatch(setLoadingTrueAction());
      const { status, data } = await editPassengerUserService(objData);
      if (status === 200) {
        toastSuccessMessage(data.resultMessage);
        handleGetPassengers();
        handleHideModal();
      }
    } catch ({ response }) {
      if (response) {
        if (response.status) {
          toastErrorMessage(response.data.resultMessage);
        }
      }
      dispatch(setLoadingFalseAction());
    }
  };

  // Delete Passenger
  const handleDeletePassenger = async (id) => {
    try {
      dispatch(setLoadingTrueAction());
      const { status, data } = await deletePassengerUserService(id);
      if (status === 200) {
        toastSuccessMessage(data.resultMessage);
        handleGetPassengers();
      }
    } catch ({ response }) {
      if (response) {
        if (response.status) {
          toastErrorMessage(response.data.resultMessage);
        }
      }
      dispatch(setLoadingFalseAction());
    }
  };

  return (
    <passengerUserContext.Provider
      value={{
        passengers,
        setPassengers,
        id,
        setId,
        filter,
        setFilter,
        sort,
        setSort,
        isShowModal,
        setIsShowModal,
        handleShowModal,
        handleHideModal,
        handleGetPassengers,
        handleCreatePassenger,
        handleEditPassenger,
        handleDeletePassenger,
      }}
    >
      {children}
    </passengerUserContext.Provider>
  );
};

export default PassengerUserContext;
